import React, { useState, useEffect, forwardRef, useRef, useImperativeHandle } from 'react';
import { withTheme } from "@rjsf/core";
import Form from '@rjsf/mui';
import validator from "@rjsf/validator-ajv8";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem
} from "@mui/material";
import { ExpandLessRounded, ExpandMoreRounded, Edit as EditIcon } from '@mui/icons-material';
import AssetExplorer from '../../components/common/AssetExplorer';
// import { useParams } from "react-router-dom";

const CustomStringField = ({
  idSchema,
  uiSchema,
  formData,
  required,
  disabled,
  readonly,
  autofocus,
  onChange,
  schema,
  rawErrors = [],
}) => {
  return (
    <TextField
      id={idSchema.$id}
      label={schema.title}
      required={required}
      disabled={disabled || readonly}
      autoFocus={autofocus}
      value={formData || ''}
      onChange={(event) => onChange(event.target.value)}
      error={rawErrors.length > 0}
      helperText={rawErrors.length > 0 ? rawErrors.join(', ') : undefined}
      fullWidth
      size="small"
      variant="standard"
      sx={{
        m: 0,
        mt: 2,
        p: 0,
        input: { color: 'white' }
      }}
      margin="dense"
    />
  );
};

const CustomObjectFieldTemplate = ({ properties }) => {
  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0, margin: 0, padding: 0 }}>
      {properties.map((element) => (
        <div key={element.name} style={{ margin: 0, padding: 0, width: '100%' }}>
          {element.content}
        </div>
      ))}
    </div>
  );
};

const CustomFieldTemplate = (props) => {
  const {
    id,
    classNames,
    label,
    help,
    required,
    description,
    errors,
    children,
    schema
  } = props;

  const nestingLevel = id.split('_').length - 2;
  const displayLabel = schema.type === 'object' && nestingLevel !== 1

  console.log('Nesting level for', id, ':', nestingLevel);

  const [isOpen, setIsOpen] = useState(true);

  return (
    <Box
      id={id}
      className={classNames}
      sx={{
        background: nestingLevel === 2 ? '#FFFFFF10' : '',
        p: nestingLevel === 3 ? 1 : nestingLevel === 2 ? 1 : 0,
        m: 0,
        width: '100%',
        pl: nestingLevel === 2 ? 1 : nestingLevel * 0.2,
        borderRadius: nestingLevel === 2 ? 2 : 0,
        mt: nestingLevel === 2 ? 2.5 : '',
      }}>
      {displayLabel && label &&
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }} >
          <Typography variant={nestingLevel === 2 ? 'h5' : 'h6'} sx={{ fontWeight: 'bold' }}>
            {label}{required ? "*" : null}
          </Typography>
          <Tooltip title="Toggle Section">
            <IconButton
              size="small"
              sx={{
                m: 0, borderRadius: '7px', p: 0, mr: 1
              }}
              onClick={() => setIsOpen(!isOpen)}
            >
              {!isOpen ? <ExpandMoreRounded /> : <ExpandLessRounded />}
            </IconButton>
          </Tooltip>
        </Box>
      }
      {description}
      {isOpen && children}
      {errors}
      {help}
    </Box>
  );
};

const CustomTextWidget = ({
  id,
  placeholder,
  value,
  required,
  disabled,
  readonly,
  label,
  onChange,
}) => (
  <TextField
    id={id}
    label={label}
    required={required}
    disabled={disabled || readonly}
    placeholder={placeholder}
    value={value !== undefined ? value : ''}
    onChange={(e) => onChange(e.target.value)}
    fullWidth
    size={'small'}
    variant={'standard'}
    sx={{
      backgroundColor: '',
      input: { color: 'white' },
      m: 0,
      p: 0
    }}
    margin={'dense'}
  />
);

const uiSchema = (gfxName) => {
  return {
    "ui:options": {
      "title": false,
      "description": false,
      "label": false,
      "submitButtonOptions": {
        "norender": true
      }
    },
    [gfxName]: {
      "ui:options": {
        "label": false
      },
      "Main": {
        "ui:options": {
          "label": false
        },
      }
    }
  };
}

const widgetsBase = {
  TextWidget: CustomTextWidget,
};

// const Form = withTheme({
//   widgets: { TextWidget: CustomTextWidget },
//   fields: { StringField: CustomStringField },
//   templates: {
//     FieldTemplate: CustomFieldTemplate,
//     ObjectFieldTemplate: CustomObjectFieldTemplate,
//   }
// });

const JsonForm = forwardRef(({ sharedState, payload, setPayload, captionId, pluginConfig, assetOptions, organizationName, sceneName, scenePath }, ref) => {
  const [schema, setSchema] = useState(null);
  const formRef = useRef();
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);
  const assetDialogFieldRef = useRef(null);
  const assetLeafKeysRef = useRef([]);
  // const { fileName, captionId } = useParams(); 
  // Async function to initialize the schema

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (formRef.current) {
        formRef.current.submit();
      }
    }
  }));

  useEffect(() => {
    const initializeSchema = async () => {
      try {
        const generatedSchema = await generateJsonSchema(sharedState);
        setSchema(generatedSchema);
      } catch (error) {
        console.error("Error generating schema:", error);
      }
    };

    if (sharedState) {
      setSchema(null)
      initializeSchema();
    }
  }, [sharedState, pluginConfig, assetOptions]);

  const prefixTypeMap = {
    str: "string",
    i: "integer",
    b: "boolean",
  };

  const widgets = {
    ...widgetsBase,
    SelectWidget: (props) => {
      const { id, onChange, value, required, disabled, readonly, label, options } = props;
      const lastKey = (id || '').split('_').pop();
      const isAssetLeaf = Array.isArray(assetLeafKeysRef.current) && assetLeafKeysRef.current.includes(lastKey);
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <TextField
            select
            fullWidth
            size={'small'}
            variant={'standard'}
            label={label}
            required={required}
            disabled={disabled || readonly}
            value={value !== undefined ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            sx={{ m: 0, p: 0, input: { color: 'white' } }}
            margin={'dense'}
          >
            {(options?.enumOptions || []).map((opt) => {
              const valueStr = String(opt.value || '');
              const isImage = /\.(png|jpg|jpeg|gif|webp|svg|avif)$/i.test(valueStr);
              const baseUrl = process.env.REACT_APP_SCENE_RENDERER_URL;
              const imgUrl = (isImage && baseUrl && scenePath)
                ? `${baseUrl}${scenePath}/assets/${valueStr}`
                : null;
              return (
                <MenuItem key={valueStr} value={opt.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    {imgUrl && (
                      <Box
                        component="img"
                        src={imgUrl}
                        alt={opt.label}
                        sx={{
                          width: 28,
                          height: 28,
                          objectFit: 'cover',
                          borderRadius: 0.5,
                          border: '1px solid rgba(255,255,255,0.2)'
                        }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {opt.label}
                    </Typography>
                  </Box>
                </MenuItem>
              );
            })}
          </TextField>
          {isAssetLeaf && (
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => {
                  assetDialogFieldRef.current = { id, onChange };
                  setAssetDialogOpen(true);
                }}
                sx={{ ml: 1 }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      );
    }
  };

  const generateJsonSchema = async (data) => {

    const newSchema = {
      $schema: "http://json-schema.org/draft-07/schema#",
      type: "object",
      properties: {},
      required: [],
    };

    const dropdownLeafKeys = pluginConfig?.objectStructure ? findLeafKeys(pluginConfig.objectStructure) : [];
    assetLeafKeysRef.current = dropdownLeafKeys;

    for (const key of Object.keys(data)) {
      const value = data[key];
      newSchema.properties[key] = await inferSchema(key, value, dropdownLeafKeys);
      const cleanedKey = removePrefix(key);

      newSchema.required.push(cleanedKey);
    }

    console.log('newschema', data, newSchema)

    return newSchema;
  };

  const inferSchema = async (key, value, dropdownLeafKeys = []) => {
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      const properties = {};
      const requiredKeys = [];

      for (const [nestedKey, nestedValue] of Object.entries(value)) {
        properties[(nestedKey)] = await inferSchema(nestedKey, nestedValue, dropdownLeafKeys); // Await for async inference
        // requiredKeys.push(removePrefix(nestedKey));
      }

      return {
        type: "object",
        properties,
        required: requiredKeys,
      };
    } else if (Array.isArray(value)) {
      return {
        type: "array",
        items: value.length > 0 ? await inferSchema(key, value[0], dropdownLeafKeys) : {},
      };
    } else {
      const baseType = extractPrefix(key) || typeof value;
      const baseSchema = {
        type: baseType,
        title: getTitle(key)
      };
      if (dropdownLeafKeys.includes(key)) {
        if (Array.isArray(assetOptions) && assetOptions.length > 0) {
          return {
            ...baseSchema,
            type: 'string',
            enum: assetOptions
          };
        }
        return baseSchema;
      }
      return baseSchema;
    }
  };

  function findLeafKeys(obj) {
    const leaves = [];
    function traverse(node) {
      if (node && typeof node === 'object' && !Array.isArray(node)) {
        for (const k of Object.keys(node)) {
          const val = node[k];
          if (val && typeof val === 'object') {
            traverse(val);
          } else {
            leaves.push(k);
          }
        }
      }
    }
    traverse(obj);
    return leaves;
  }

  function getTitle(name) {
    return camelCaseToCamelCaseLabel(removePrefix(name))
  }

  function camelCaseToCamelCaseLabel(str) {
    return str
      .replace(/([a-z])([A-Z])/g, '$1 $2')  // Insert space before capital letters
      .replace(/^./, (match) => match.toUpperCase()); // Capitalize first letter
  }

  function updatePayload(data, captionId) {
    const updatePayload = {
      [captionId]: data[captionId]
    }
    console.log("Update Payload:", updatePayload);

    setPayload((prevPayload) => ({
      ...prevPayload,
      ...updatePayload
    }));
  }

  const extractPrefix = (key) => {
    console.log("🚀 ~ extractPrefix ~ key:", key)
    const prefixRegex = new RegExp(`^(${Object.keys(prefixTypeMap).join('|')})`);
    const match = key.match(prefixRegex);
    return match ? prefixTypeMap[match[0]] : null; // Return the matched prefix or null
  };


  const extractToChangeField = (key) => {




  }

  const removePrefix = (key) => {
    // return key;
    const prefixRegex = new RegExp(`^(${Object.keys(prefixTypeMap).join('|')})`);
    return key.replace(prefixRegex, ""); // Remove prefix if found
  };

  return (
    <>
      {schema !== null ? (
        <>
          <Box

          >
            <Form
              ref={formRef}
              id="rjsf-form"
              schema={schema}
              uiSchema={uiSchema(captionId)}
              validator={validator}
              formData={payload}
              onSubmit={(e) => updatePayload(e.formData, captionId)}
              onError={(e) => console.log("Form Errors:", e)}
              liveValidate={true}
              widgets={widgets}
              noHtml5Validate
              hideSubmitButton
              style={{ width: '100%', margin: 0, padding: 0 }}
            >
            </Form>
          </Box>
          <Dialog open={assetDialogOpen} onClose={() => setAssetDialogOpen(false)} fullWidth maxWidth="lg">
            <DialogContent>
              <AssetExplorer
                organizationName={organizationName}
                sceneName={sceneName}
                initialPath={pluginConfig?.baseAssetPath ? `assets/${pluginConfig.baseAssetPath.replace(/\/+$/, '')}/` : 'assets'}
                hideBreadcrumbs={true}
                onFileSelect={(file) => {
                  try {
                    if (!file || !file.path || !assetDialogFieldRef.current) return;
                    let rel = file.path;
                    if (rel.startsWith('assets/')) rel = rel.substring(7);
                    const finalPath = rel.startsWith('assets/') ? rel : `assets/${rel}`;
                    assetDialogFieldRef.current.onChange(finalPath);
                    setAssetDialogOpen(false);
                  } catch (e) {
                    console.warn('Failed to set asset from explorer', e);
                  }
                }}
              />
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={() => setAssetDialogOpen(false)}>Cancel</Button>
            </DialogActions>
          </Dialog>
        </>
      ) : (
        <p>Loading...</p>
      )}
    </>
  );
});

export default JsonForm;